import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMaintenanceMode, useIsSystemAdmin } from '../hooks/useMaintenanceMode';
import MaintenancePage from './MaintenancePage';

/**
 * Routes that must remain reachable during maintenance so an admin can
 * authenticate (and turn maintenance off). Without these the maintenance
 * page would intercept every path, leaving no way to log in.
 */
const AUTH_BYPASS_PATHS = ['/login', '/auth/callback', '/reset-password', '/set-password'];

/**
 * Wrapper component that checks maintenance mode and shows maintenance page
 * to non-admin users when maintenance is active
 */
const MaintenanceWrapper = ({ children }) => {
  const { maintenanceMode, loading: maintenanceLoading } = useMaintenanceMode();
  const { isAdmin, loading: adminLoading } = useIsSystemAdmin();
  const location = useLocation();

  // Always allow auth-related routes through so users (including admins) can log in
  const isAuthRoute = AUTH_BYPASS_PATHS.includes(location.pathname);

  // Show loading state while checking
  if (maintenanceLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If maintenance mode is enabled and user is not an admin, show maintenance page
  // (unless they are on an auth route, so they can log in to disable maintenance)
  if (maintenanceMode?.enabled && !isAdmin && !isAuthRoute) {
    // Check if staff access is allowed
    if (maintenanceMode.allow_staff) {
      // Would need to check if user is staff here
      // For now, we'll just show maintenance page for non-admins
      return <MaintenancePage />;
    }
    return <MaintenancePage />;
  }

  // Otherwise, render the children (normal app)
  return <>{children}</>;
};

export default MaintenanceWrapper;
